import React from 'react';
import { WORLD_MAP_VIEWBOX, WORLD_MAP_INNER } from './worldMapPaths';

const GOLD = '#f5c542';

const focusTracking = () => {
  const el = document.getElementById('hero-tracking-input');
  if (!el) return;
  const wrap = el.closest('.tracking-wrap') || el.parentElement;
  el.scrollIntoView({ behavior: 'smooth', block: 'center' });
  if (wrap) wrap.classList.add('tracking-highlight');
  setTimeout(() => el.focus({ preventScroll: true }), 450);
  setTimeout(() => wrap && wrap.classList.remove('tracking-highlight'), 3000);
};

const PlaneIcon = () => (
  <path
    d="M0,-2 L10,0 L0,2 L-4,6 L-2,1 L-8,1 L-10,3 L-11,2 L-9,0 L-11,-2 L-10,-3 L-8,-1 L-2,-1 L-4,-6 Z"
    fill="#ffffff"
    stroke={GOLD}
    strokeWidth="0.6"
  />
);

const ShipIcon = () => (
  <g>
    <path d="M-9,2 L9,2 L6,7 L-6,7 Z" fill={GOLD} stroke="#0a1836" strokeWidth="0.5" />
    <rect x="-4" y="-3" width="8" height="5" rx="1" fill="#ffffff" />
    <rect x="-1.2" y="-7" width="2.4" height="4" fill={GOLD} />
  </g>
);

export const InteractiveWorldMap = ({ onTransportClick }) => {
  const handleClick = (e) => {
    e.stopPropagation();
    (onTransportClick || focusTracking)();
  };
  const tp = { onClick: handleClick, style: { cursor: 'pointer', pointerEvents: 'all' } };

  return (
    <div
      className="relative rounded-2xl overflow-hidden border border-amber-400/25 shadow-2xl"
      style={{ background: 'radial-gradient(ellipse at 50% 40%, #0d2250 0%, #071023 100%)' }}
      data-testid="interactive-world-map"
    >
      <svg viewBox={WORLD_MAP_VIEWBOX} className="w-full h-auto block" xmlns="http://www.w3.org/2000/svg">
        <defs>
          <filter id="goldGlow" x="-40%" y="-40%" width="180%" height="180%">
            <feGaussianBlur stdDeviation="1.6" result="blur" />
            <feMerge>
              <feMergeNode in="blur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>

        {/* Graticule (ocean grid) */}
        <g stroke="rgba(245,197,66,0.06)" strokeWidth="0.5">
          {Array.from({ length: 16 }).map((_, i) => (
            <line key={`v${i}`} x1={30.767 + (i * 784.077) / 15} y1="241.591" x2={30.767 + (i * 784.077) / 15} y2="700.218" />
          ))}
          {Array.from({ length: 10 }).map((_, i) => (
            <line key={`h${i}`} x1="30.767" y1={241.591 + (i * 458.627) / 9} x2="814.844" y2={241.591 + (i * 458.627) / 9} />
          ))}
        </g>

        {/* Base world map (real continent contours) */}
        <g
          className="world-map-base"
          dangerouslySetInnerHTML={{ __html: WORLD_MAP_INNER }}
        />

        {/* Overlay: routes, dots, transport. Coordinates tuned to real country positions. */}
        <g>
          {/* Sea routes (ocean only) */}
          <g fill="none" stroke="rgba(245,197,66,0.5)" strokeWidth="1.1" strokeDasharray="4,4">
            <path id="seaChina" d="M655,460 C645,500 560,516 480,506 C432,499 400,470 430,442 C450,424 466,416 474,410" />
            <path id="seaUSA" d="M205,382 C285,422 362,432 420,426 C450,423 466,418 474,412" />
            <animate attributeName="stroke-dashoffset" from="16" to="0" dur="1.6s" repeatCount="indefinite" />
          </g>
          {/* Air routes */}
          <g fill="none" stroke="rgba(255,255,255,0.35)" strokeWidth="0.9" strokeDasharray="1.5,6" strokeLinecap="round">
            <path id="airGZ" d="M652,448 Q565,362 476,397" />
            <path id="airBJ" d="M668,388 Q570,350 476,397" />
            <path id="airUS" d="M150,360 Q312,318 476,397" />
          </g>

          {/* City dots */}
          <g filter="url(#goldGlow)">
            <circle cx="668" cy="388" r="3" fill={GOLD}><animate attributeName="r" values="3;4.5;3" dur="2.2s" repeatCount="indefinite" /></circle>
            <circle cx="652" cy="448" r="3" fill={GOLD}><animate attributeName="r" values="3;4.5;3" dur="2.6s" repeatCount="indefinite" /></circle>
            <circle cx="448" cy="389" r="2.6" fill={GOLD} />
            <circle cx="476" cy="397" r="3.2" fill="#ffd700"><animate attributeName="r" values="3.2;5;3.2" dur="1.8s" repeatCount="indefinite" /></circle>
            <circle cx="474" cy="408" r="2.2" fill={GOLD} />
            <circle cx="150" cy="360" r="3" fill={GOLD}><animate attributeName="r" values="3;4.5;3" dur="2.4s" repeatCount="indefinite" /></circle>
          </g>
          <g fontSize="9" fontWeight="600" fill={GOLD} style={{ fontFamily: 'inherit' }}>
            <text x="676" y="385">Пекін</text>
            <text x="628" y="461">Гуанчжоу</text>
            <text x="416" y="383">Польща</text>
            <text x="483" y="394" fill="#ffd700" fontSize="10">Україна</text>
            <text x="478" y="418" fontSize="8">Одеса</text>
            <text x="126" y="353">США</text>
            <text x="612" y="422" fontSize="11" opacity="0.85">Китай</text>
          </g>

          {/* Planes (clickable) */}
          <g {...tp} data-testid="map-plane-1">
            <circle r="12" fill="transparent" />
            <PlaneIcon />
            <animateMotion id="p1a" dur="8s" begin="0s;p1b.end+0.8s" rotate="auto" fill="freeze"><mpath href="#airGZ" /></animateMotion>
            <animateMotion id="p1b" dur="7s" begin="p1a.end+0.8s" rotate="auto" fill="freeze"><mpath href="#airBJ" /></animateMotion>
          </g>
          <g {...tp} data-testid="map-plane-2">
            <circle r="12" fill="transparent" />
            <PlaneIcon />
            <animateMotion dur="9s" begin="2.5s" rotate="auto" repeatCount="indefinite"><mpath href="#airUS" /></animateMotion>
          </g>

          {/* Ships (clickable) */}
          <g {...tp} data-testid="map-ship-1">
            <circle r="12" fill="transparent" />
            <ShipIcon />
            <animateMotion dur="16s" begin="0s" repeatCount="indefinite"><mpath href="#seaChina" /></animateMotion>
          </g>
          <g {...tp} data-testid="map-ship-2">
            <circle r="12" fill="transparent" />
            <ShipIcon />
            <animateMotion dur="13s" begin="4s" repeatCount="indefinite"><mpath href="#seaUSA" /></animateMotion>
          </g>
        </g>
      </svg>

      <div className="absolute bottom-1.5 left-0 right-0 text-center pointer-events-none">
        <p className="text-amber-300/70 text-[10px] sm:text-xs px-2">
          ✈ Натисніть на літак або корабель, щоб відстежити свою посилку
        </p>
      </div>
    </div>
  );
};

export default InteractiveWorldMap;
